import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, FileXls, DownloadSimple, Archive, Headset } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useLineups } from './daily/useLineups';
import { StatsBar } from './daily/StatsBar';
import { FiltersBar } from './daily/FiltersBar';
import { CompanyCards } from './daily/CompanyCards';
import { LineupTable } from './daily/LineupTable';
import { LineupByReport } from './daily/LineupByReport';
import { AddLineupDialog } from './daily/AddLineupDialog';
import { UploadDialog } from './daily/UploadDialog';
import { NotGoingDialog } from './daily/NotGoingDialog';
import { fmtDate, downloadBlob, aggregateCompanies, aggregateReport } from './daily/helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const btn = 'border-2 border-[#0A0A0A] rounded-md font-bold px-4 py-2 shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-all active:translate-y-0.5 active:shadow-none';

const DailyFollowup = () => {
  const { isOwner } = useAuth();
  const navigate = useNavigate();
  const L = useLineups(null);
  const [addOpen, setAddOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [notGoingRow, setNotGoingRow] = useState(null);

  const onStatusChange = (row, status) => {
    if (status === 'Not Going') { setNotGoingRow(row); return; }
    L.updateStatus(row, status);
  };

  const exportToday = async () => {
    try {
      const { data } = await axios.get(`${API}/lineups/export.xlsx`, { params: { date: L.data?.date }, responseType: 'blob' });
      downloadBlob(data, `TulsiJobs_Lineups_${L.data?.date}.xlsx`);
      toast.success('Exported today\'s lineups');
    } catch { toast.error('Export failed'); }
  };

  if (L.loading) return <div className="flex items-center justify-center h-full"><div className="text-xl font-bold text-white">Loading...</div></div>;

  const visibleCompanies = aggregateCompanies(L.rowsNoCompany);

  return (
    <div data-testid="daily-followup-page">
      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-1 flex items-center gap-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            <Headset size={44} weight="duotone" className="text-[#A3E635]" /> Daily Follow-up
          </h1>
          <p className="text-base font-medium text-white">
            Today · <span className="text-[#FDE047] font-black" data-testid="daily-date">{fmtDate(L.data?.date)}</span> · live telecalling board. Auto-archives at midnight.
          </p>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button data-testid="open-archive-btn" onClick={() => navigate('/archive')} className={`${btn} bg-white text-black`}>
            <Archive size={20} weight="bold" className="mr-2" /> Archive
          </Button>
          <Button data-testid="export-today-btn" onClick={exportToday} className={`${btn} bg-[#93C5FD] text-black`}>
            <DownloadSimple size={20} weight="bold" className="mr-2" /> Export
          </Button>
          {isOwner && (
            <Button data-testid="upload-excel-btn" onClick={() => setUploadOpen(true)} className={`${btn} bg-[#FDE047] text-black`}>
              <FileXls size={20} weight="bold" className="mr-2" /> Upload Excel
            </Button>
          )}
          <Button data-testid="add-lineup-btn" onClick={() => setAddOpen(true)} className={`${btn} bg-[#A3E635] text-black`}>
            <Plus size={20} weight="bold" className="mr-2" /> Add Lineup
          </Button>
        </div>
      </div>

      <StatsBar summary={L.data?.summary} />

      <FiltersBar
        showDate={false}
        company={L.company} setCompany={L.setCompany} companies={L.options.companies}
        lineupBy={L.lineupBy} setLineupBy={L.setLineupBy} lineupBys={L.options.lineupBys}
        telecaller={L.telecaller} setTelecaller={L.setTelecaller} telecallers={L.options.telecallers}
        onClear={L.clearFilters} count={L.rows.length} total={L.data?.rows?.length || 0}
      />

      <section className="mb-6" data-testid="companies-section">
        <h2 className="text-base md:text-lg font-black text-white mb-3 uppercase tracking-wider">Companies · click a card to open</h2>
        <CompanyCards companies={visibleCompanies} selected={L.company} onSelect={L.setCompany} />
      </section>

      <section className="mb-6">
        <LineupTable
          rows={L.rows} company={L.company} onClearCompany={() => L.setCompany('')}
          readOnly={false} isOwner={isOwner}
          onStatusChange={onStatusChange} onDelete={L.remove} onSendToTracker={L.sendToTracker}
        />
      </section>

      <section className="mb-6">
        <LineupByReport report={aggregateReport(L.rows)} title={`Lineup By Performance · ${fmtDate(L.data?.date)}`} />
      </section>

      <AddLineupDialog open={addOpen} onOpenChange={setAddOpen} today={L.data?.today} meta={L.meta} onSubmit={L.add} />
      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onUploaded={() => L.reload()} />
      <NotGoingDialog
        row={notGoingRow}
        onCancel={() => setNotGoingRow(null)}
        onConfirm={async (reason) => { const r = notGoingRow; setNotGoingRow(null); await L.updateStatus(r, 'Not Going', reason); }}
      />
    </div>
  );
};

export default DailyFollowup;
